// What the agent asks the portal to do with one of its own create forms: open it, filled in with the values
// it proposes. Nothing is submitted: the form is the user's, and so is the Send for approval it ends with.
// Keep it import-free of React, so `node --test` can run draftActions.test.ts against it directly.
//
// The proposal is CLIENT-BOUND but AGENT-SUPPLIED, so nothing in it is trusted: every field is checked
// against the form that is actually mounted, every value is cleaned and capped, and the path it names is only
// followed when the app's own registry agrees that is where that form lives. What cannot be matched is
// dropped and named, so a step in the panel never claims more than the form is showing.

import { clean, offerStore, pendingStore } from './proposalChannel.ts';

/** The key the agent's tool-call chunk carries its proposal under. */
export const OPEN_CRUD_DRAFT = 'openCrudDraft';

/** The action enso matches a confirmed step to. It is also the step's title, so it reads as one. */
export const OPEN_CREATE_FORM = 'Open the create form';

/**
 * JSON Schema enso checks the call against before offering it. One argument, and a readable one: the panel
 * prints a step's arguments under its title, so anything technical in here is technical on the user's screen.
 */
export const OPEN_CREATE_FORM_PARAMETERS: Record<string, unknown> = {
  type: 'object',
  properties: { open: { type: 'string' } },
  required: ['open'],
};

/** Longest value carried into a field, and the most fields honoured. A create form is a handful of fields. */
export const MAX_DRAFT_FIELDS = 30;
export const MAX_DRAFT_VALUE_LENGTH = 200;

/** What a form field can be filled in with. Anything else is a shape no field takes. */
export type DraftValue = string | number | boolean;

/** The proposal as it arrives on the wire. Every field is checked before it is used. */
export interface CrudDraftInstruction {
  entity: string;
  form?: string;
  path?: string;
  values?: Record<string, unknown>;
  /** The user has agreed to be taken to this form. Without it a form that is not on screen is left alone. */
  openForm?: boolean;
}

/** What the form was actually filled in with, once the proposal was checked against it. */
export interface AppliedCrudDraft {
  entity: string;
  values: Record<string, DraftValue>;
  /** Fields the proposal named that this form does not have. */
  dropped: string[];
}

/**
 * How a mounted create form is reached. A reader rather than the opener itself, for the reason the list
 * registry keeps one: what a page hands over is rebuilt as it renders, so an object held from mount answers
 * for the page as it was then.
 */
export type ReadDraftController = () => DraftController | null;

/** What a page offers the assistant: the fields its create form has, and opening it filled in. */
export interface DraftController {
  fields: () => string[];
  open: (values: Record<string, DraftValue>) => void;
}

/** The proposal carried by a tool-call chunk, or null when the chunk carries none this can act on. */
export const readCrudDraftInstruction = (args: unknown): CrudDraftInstruction | null => {
  if (args === null || typeof args !== 'object') return null;

  const payload = (args as Record<string, unknown>)[OPEN_CRUD_DRAFT];
  if (payload === null || typeof payload !== 'object') return null;

  const { entity, form, path, values, openForm } = payload as Record<string, unknown>;
  if (typeof entity !== 'string' || clean(entity, MAX_DRAFT_VALUE_LENGTH) === '') return null;

  return {
    entity: clean(entity, MAX_DRAFT_VALUE_LENGTH),
    form: typeof form === 'string' ? clean(form, MAX_DRAFT_VALUE_LENGTH) : undefined,
    path: typeof path === 'string' ? path : undefined,
    values: values !== null && typeof values === 'object' && !Array.isArray(values) ? (values as Record<string, unknown>) : undefined,
    openForm: openForm === true,
  };
};

// A number reaches a number field as a number: a rate typed into the form as text would fail its own
// validation, and the user would be left correcting the assistant's typing rather than reading its proposal.
const draftValue = (value: unknown): DraftValue | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return null;

  const text = clean(value, MAX_DRAFT_VALUE_LENGTH);
  return text === '' ? null : text;
};

/**
 * The values the mounted form can take, keyed by its own field names, and the ones it cannot. The fields come
 * from the form rather than from a registry the agent holds a copy of, so a field the form does not offer in
 * create mode is refused here whatever the agent believes about it.
 */
export const resolveDraftValues = (
  instruction: CrudDraftInstruction,
  controller: DraftController,
): { values: Record<string, DraftValue>; dropped: string[] } => {
  const fields = controller.fields();
  const values: Record<string, DraftValue> = {};
  const dropped: string[] = [];

  Object.entries(instruction.values ?? {}).forEach(([field, value]) => {
    if (Object.keys(values).length >= MAX_DRAFT_FIELDS) return;

    const key = clean(field, MAX_DRAFT_VALUE_LENGTH);
    const wanted = draftValue(value);
    if (key === '' || wanted === null || !fields.includes(key)) dropped.push(field);
    else values[key] = wanted;
  });

  return { values, dropped };
};

/**
 * Opens the form the proposal names, filled in with what it can take. Returns what the form was given, or
 * null when the proposal named nothing it has: a create form opened empty over a proposal the user asked for
 * says the assistant did something it did not do.
 */
export const applyCrudDraft = (instruction: CrudDraftInstruction, controller: DraftController): AppliedCrudDraft | null => {
  const { values, dropped } = resolveDraftValues(instruction, controller);
  if (Object.keys(values).length === 0) return null;

  controller.open(values);
  return { entity: instruction.entity, values, dropped };
};

/** What the offer would do, in the words the step shows and the user judges it by. */
export const describeDraftOffer = (instruction: CrudDraftInstruction): string => {
  const name = instruction.form ?? instruction.entity;
  const named = Object.keys(instruction.values ?? {});

  return named.length === 0 ? name : `${name}, filled in with ${named.join(', ')}`;
};

/** The step's result line: what the user can see happened, including what the form could not take. */
export const describeDraftApplied = (applied: AppliedCrudDraft, formName: string | undefined): string => {
  const name = formName ?? applied.entity;
  const filled = `${name} form opened, filled in with ${Object.keys(applied.values).join(', ')}`;

  return applied.dropped.length === 0 ? filled : `${filled}; this form has no ${applied.dropped.join(', ')}`;
};

const offers = offerStore<CrudDraftInstruction>(
  instruction => describeDraftOffer(instruction),
  instruction => ({ ...instruction, openForm: true }),
);

/** The call the panel offers: one readable argument, with the proposal kept against it. */
export const offerOpenFormCall = offers.offer;

/** The proposal behind a confirmed call, or null when it is no longer remembered. */
export const takeOpenFormOffer = offers.take;

// A proposal for a page that is not on screen waits here until that page arrives. It survives the one
// navigation it takes to get there and nothing more: it expires, it is dropped once drained, and a newer one
// replaces it.

const pending = pendingStore<CrudDraftInstruction>();

/** Holds a proposal for a page that is not on screen, replacing any older one. */
export const holdCrudDraft = pending.hold;

/** The proposal waiting for this page, if it is still current, with its deadline. Taking it clears it. */
export const takeCrudDraft = pending.take;

export const clearPendingCrudDraft = (): void => {
  pending.clear();
  offers.forget();
};

/**
 * Opens the proposal waiting for this page, and says whether the caller is done. False means one is still
 * waiting, so the caller retries: a page reaches this before the form it registers can accept anything, and
 * a proposal dropped on that first attempt would be dropped on a page that is about to honour it.
 */
export const drainCrudDraft = (entityType: string, read: ReadDraftController): boolean => {
  const waiting = takeCrudDraft(entityType);
  if (waiting === null) return true;

  const controller = read();
  if (controller === null) {
    holdCrudDraft(waiting.instruction, waiting.expiresAt);
    return false;
  }

  if (applyCrudDraft(waiting.instruction, controller) !== null) return true;

  holdCrudDraft(waiting.instruction, waiting.expiresAt);
  return false;
};
